/**
 * UndoManager - Lightweight delta-based undo/redo for EdgeDocs
 *
 * Architecture:
 * - Stores minimal deltas (position + text), NOT full document or cache snapshots
 * - Undo/redo are just inverse edit operations through the same WASM pipeline
 * - WASM re-layouts affected paragraphs, cache eviction handles the rest
 * - Coalesces rapid typing into single undo units
 * - Supports formatting changes (line spacing, font size, etc.) as undoable operations
 *
 * Why this works for EdgeDocs:
 * - WASM is the single source of truth for layout
 * - Incremental layout is already fast (only affected paragraphs)
 * - No need to store/restore cache - just re-layout via normal flow
 */

import { InputLogger } from '../../../../utils/loggers/InputLogger';

/** Formatting properties that can be changed on a paragraph */
export interface ParagraphFormatting {
  lineSpacing?: number;
  fontSizePx?: number;
  fontFamily?: string;
}

/** A single paragraph's formatting change (before/after) */
export interface ParagraphFormattingChange {
  paragraphId: string;
  before: ParagraphFormatting;
  after: ParagraphFormatting;
}

/** Text edit command (insert, delete, replace) */
export interface TextUndoCommand {
  /** Unique ID for this command */
  id: string;

  /** Type of operation (for debugging/grouping) */
  type: 'insert' | 'delete' | 'replace';

  /** Position where the edit occurred */
  position: number;

  /** Text that was deleted (needed to restore on undo) */
  deletedText: string;

  /** Text that was inserted (needed to restore on redo) */
  insertedText: string;

  /** Timestamp for coalescing */
  timestamp: number;

  /** Cursor position before the edit */
  cursorBefore: number;

  /** Cursor position after the edit */
  cursorAfter: number;
}

/** Formatting change command (line spacing, font size, etc.) */
export interface FormattingUndoCommand {
  /** Unique ID for this command */
  id: string;

  /** Type of operation */
  type: 'formatting';

  /** Paragraphs affected and their before/after formatting */
  changes: ParagraphFormattingChange[];

  /** Timestamp */
  timestamp: number;

  /** Cursor position (preserved during formatting changes) */
  cursorPosition: number;
}

/** Union of all command types */
export type UndoCommand = TextUndoCommand | FormattingUndoCommand;

/** Type guard for text commands */
export function isTextCommand(cmd: UndoCommand): cmd is TextUndoCommand {
  return cmd.type === 'insert' || cmd.type === 'delete' || cmd.type === 'replace';
}

/** Type guard for formatting commands */
export function isFormattingCommand(cmd: UndoCommand): cmd is FormattingUndoCommand {
  return cmd.type === 'formatting';
}

export interface UndoManagerConfig {
  /** Max undo levels (default: 100) */
  maxUndoLevels?: number;

  /** Coalesce timeout in ms - edits within this window are grouped (default: 500ms) */
  coalesceTimeoutMs?: number;

  /** Callback to apply an edit to the document */
  applyEdit: (position: number, deleteCount: number, insertText: string) => Promise<void>;

  /** Callback to apply formatting changes to paragraphs */
  applyFormatting?: (updates: Array<{ id: string } & ParagraphFormatting>) => Promise<void>;

  /** Callback to set cursor position */
  setCursor: (position: number) => void;

  /** Callback to get current cursor position */
  getCursor: () => number;
}

const DEFAULT_MAX_UNDO_LEVELS = 100;
const DEFAULT_COALESCE_TIMEOUT_MS = 500;

/** Config type with optional applyFormatting */
type ResolvedConfig = Omit<Required<UndoManagerConfig>, 'applyFormatting'> & {
  applyFormatting: UndoManagerConfig['applyFormatting'];
};

export class UndoManager {
  private undoStack: UndoCommand[] = [];
  private redoStack: UndoCommand[] = [];
  private config: ResolvedConfig;
  private lastEditTimestamp: number = 0;
  private isUndoRedoInProgress: boolean = false;
  private commandIdCounter: number = 0;

  constructor(config: UndoManagerConfig) {
    this.config = {
      maxUndoLevels: config.maxUndoLevels ?? DEFAULT_MAX_UNDO_LEVELS,
      coalesceTimeoutMs: config.coalesceTimeoutMs ?? DEFAULT_COALESCE_TIMEOUT_MS,
      applyEdit: config.applyEdit,
      applyFormatting: config.applyFormatting,
      setCursor: config.setCursor,
      getCursor: config.getCursor,
    };
  }

  /**
   * Record an edit operation for undo
   * Called AFTER the edit has been applied to the document
   */
  recordEdit(
    position: number,
    deletedText: string,
    insertedText: string,
    cursorBefore: number,
    cursorAfter: number
  ): void {
    // Don't record edits triggered by undo/redo
    if (this.isUndoRedoInProgress) {
      return;
    }

    const now = Date.now();
    const type = this.getEditType(deletedText, insertedText);

    // Check if we should coalesce with previous edit
    const lastCommand = this.undoStack[this.undoStack.length - 1];
    const shouldCoalesce = lastCommand &&
      isTextCommand(lastCommand) &&
      (now - this.lastEditTimestamp) < this.config.coalesceTimeoutMs &&
      this.canCoalesce(lastCommand, position, type);

    if (shouldCoalesce && lastCommand && isTextCommand(lastCommand)) {
      // Coalesce: extend the previous command
      this.coalesceCommand(lastCommand, position, deletedText, insertedText, cursorAfter);
      InputLogger.undoManager.coalesced(lastCommand.id, type, position, deletedText.length, insertedText.length);
    } else {
      // New command
      const command: TextUndoCommand = {
        id: `undo-${++this.commandIdCounter}`,
        type,
        position,
        deletedText,
        insertedText,
        timestamp: now,
        cursorBefore,
        cursorAfter,
      };

      this.undoStack.push(command);

      // Enforce max undo levels
      while (this.undoStack.length > this.config.maxUndoLevels) {
        this.undoStack.shift();
      }

      InputLogger.undoManager.recorded(command.id, type, position, deletedText.length, insertedText.length, {
        stackSize: this.undoStack.length
      });
    }

    // Clear redo stack on new edit
    this.redoStack = [];
    this.lastEditTimestamp = now;
  }

  /**
   * Record a formatting change for undo
   * Called AFTER the formatting has been applied to the document
   *
   * @param changes - Array of paragraph formatting changes with before/after states
   */
  recordFormatting(changes: ParagraphFormattingChange[]): void {
    // Don't record formatting triggered by undo/redo
    if (this.isUndoRedoInProgress) {
      return;
    }

    if (changes.length === 0) {
      return;
    }

    const command: FormattingUndoCommand = {
      id: `undo-${++this.commandIdCounter}`,
      type: 'formatting',
      changes,
      timestamp: Date.now(),
      cursorPosition: this.config.getCursor(),
    };

    this.undoStack.push(command);

    // Enforce max undo levels
    while (this.undoStack.length > this.config.maxUndoLevels) {
      this.undoStack.shift();
    }

    InputLogger.undoManager.recorded(command.id, 'formatting', 0, 0, changes.length, {
      stackSize: this.undoStack.length,
      paragraphIds: changes.map(c => c.paragraphId)
    });

    // Clear redo stack on new edit
    this.redoStack = [];
    this.lastEditTimestamp = Date.now();
  }

  /**
   * Undo the last operation
   * Returns true if undo was performed, false if nothing to undo
   */
  async undo(): Promise<boolean> {
    const command = this.undoStack.pop();
    if (!command) {
      InputLogger.undoManager.nothingToUndo();
      return false;
    }

    this.isUndoRedoInProgress = true;
    try {
      if (isTextCommand(command)) {
        // Text undo
        InputLogger.undoManager.undoExecuted(
          command.id,
          command.type,
          command.position,
          command.insertedText.length,
          command.deletedText.length
        );

        // Apply inverse operation:
        // - Delete what was inserted
        // - Insert what was deleted
        await this.config.applyEdit(
          command.position,
          command.insertedText.length,  // Delete count
          command.deletedText            // Insert text
        );

        // Restore cursor to before position
        this.config.setCursor(command.cursorBefore);
      } else if (isFormattingCommand(command)) {
        // Formatting undo
        InputLogger.undoManager.undoExecuted(
          command.id,
          'formatting',
          0,
          command.changes.length,
          0
        );

        if (!this.config.applyFormatting) {
          console.warn('[UndoManager] Cannot undo formatting - applyFormatting callback not configured');
          // Put command back on undo stack
          this.undoStack.push(command);
          return false;
        }

        // Apply inverse formatting (restore "before" values)
        const undoUpdates = command.changes.map(change => ({
          id: change.paragraphId,
          ...change.before
        }));
        await this.config.applyFormatting(undoUpdates);

        // Restore cursor position
        this.config.setCursor(command.cursorPosition);
      }

      // Move to redo stack
      this.redoStack.push(command);

      return true;
    } finally {
      this.isUndoRedoInProgress = false;
    }
  }

  /**
   * Redo the last undone operation
   * Returns true if redo was performed, false if nothing to redo
   */
  async redo(): Promise<boolean> {
    const command = this.redoStack.pop();
    if (!command) {
      InputLogger.undoManager.nothingToRedo();
      return false;
    }

    this.isUndoRedoInProgress = true;
    try {
      if (isTextCommand(command)) {
        // Text redo
        InputLogger.undoManager.redoExecuted(
          command.id,
          command.type,
          command.position,
          command.deletedText.length,
          command.insertedText.length
        );

        // Re-apply original operation:
        // - Delete what was originally deleted
        // - Insert what was originally inserted
        await this.config.applyEdit(
          command.position,
          command.deletedText.length,   // Delete count
          command.insertedText          // Insert text
        );

        // Restore cursor to after position
        this.config.setCursor(command.cursorAfter);
      } else if (isFormattingCommand(command)) {
        // Formatting redo
        InputLogger.undoManager.redoExecuted(
          command.id,
          'formatting',
          0,
          0,
          command.changes.length
        );

        if (!this.config.applyFormatting) {
          console.warn('[UndoManager] Cannot redo formatting - applyFormatting callback not configured');
          // Put command back on redo stack
          this.redoStack.push(command);
          return false;
        }

        // Re-apply formatting (restore "after" values)
        const redoUpdates = command.changes.map(change => ({
          id: change.paragraphId,
          ...change.after
        }));
        await this.config.applyFormatting(redoUpdates);

        // Restore cursor position
        this.config.setCursor(command.cursorPosition);
      }

      // Move back to undo stack
      this.undoStack.push(command);

      return true;
    } finally {
      this.isUndoRedoInProgress = false;
    }
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all undo/redo history
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.lastEditTimestamp = 0;
    InputLogger.undoManager.cleared();
  }

  /**
   * Get current stack sizes (for debugging/UI)
   */
  getStackSizes(): { undo: number; redo: number } {
    return {
      undo: this.undoStack.length,
      redo: this.redoStack.length,
    };
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  private getEditType(deletedText: string, insertedText: string): 'insert' | 'delete' | 'replace' {
    if (deletedText.length === 0) return 'insert';
    if (insertedText.length === 0) return 'delete';
    return 'replace';
  }

  private canCoalesce(last: TextUndoCommand, newPosition: number, newType: string): boolean {
    // Only coalesce same-type operations
    if (last.type !== newType) return false;

    // For inserts: new position should be at end of last insert
    if (newType === 'insert') {
      const expectedPos = last.position + last.insertedText.length;
      return newPosition === expectedPos;
    }

    // For deletes: new position should be at start of last delete (backspace)
    // or at end of last delete range (forward delete)
    if (newType === 'delete') {
      // Backspace: position decreases by 1 each time
      if (newPosition === last.position - 1) return true;
      // Forward delete: position stays the same
      if (newPosition === last.position) return true;
    }

    return false;
  }

  private coalesceCommand(
    command: TextUndoCommand,
    newPosition: number,
    deletedText: string,
    insertedText: string,
    cursorAfter: number
  ): void {
    if (command.type === 'insert') {
      // Extend inserted text
      command.insertedText += insertedText;
      command.cursorAfter = cursorAfter;
    } else if (command.type === 'delete') {
      if (newPosition < command.position) {
        // Backspace: prepend deleted text, update position
        command.deletedText = deletedText + command.deletedText;
        command.position = newPosition;
      } else {
        // Forward delete: append deleted text
        command.deletedText += deletedText;
      }
      command.cursorAfter = cursorAfter;
    }

    command.timestamp = Date.now();
  }
}
